import { type CSSProperties, useEffect, useMemo, useState } from 'react';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Alert } from '@backstage/ui';
import { TechRadarComponent } from '@backstage-community/plugin-tech-radar';
import type { TechRadarLoaderResponse } from '@backstage-community/plugin-tech-radar-common';
import { isTechRadarEntity } from '@ns/plugin-ns-tech-radar-common';

import { CatalogTechRadarApi } from '../../lib/catalog-tech-radar-api';
import { TechRadarApiScope } from '../../lib/tech-radar-api-scope';
import { transformRadarData } from '../../lib/transform-radar-data';
import { useLiveRadarData } from '../../lib/use-live-radar-data';

import styles from './tech-radar-entity-overview-content.module.css';

/**
 * Aspect ratio used for the radar SVG. The radar component itself does not
 * enforce one — we keep the historical 0.65 (height / width) so the SVG and
 * its quadrant text-blocks stay visually balanced.
 *
 * The reciprocal (`1 / RADAR_HEIGHT_TO_WIDTH`) is forwarded to the CSS
 * module via the `--radar-aspect-ratio` custom property on the tile, so
 * the `aspect-ratio` and `width` calc in `tech-radar-entity-overview-content.module.css`
 * stay coupled to this single constant.
 */
const RADAR_HEIGHT_TO_WIDTH = 0.65;
const RADAR_ASPECT_RATIO = 1 / RADAR_HEIGHT_TO_WIDTH;
const tileStyle: CSSProperties = {
  // Custom property forwarded to the CSS module (`--radar-aspect-ratio`).
  ['--radar-aspect-ratio' as string]: RADAR_ASPECT_RATIO,
};

export interface TechRadarEntityOverviewContentProps {
  /**
   * Optional initial value for the search field. Useful for Storybook
   * stories that want to demonstrate the filter active on mount.
   */
  initialSearchText?: string;
}

export function TechRadarEntityOverviewContent(
  props: TechRadarEntityOverviewContentProps = {},
) {
  const { entity } = useEntity();
  // Callback-ref so the ResizeObserver re-attaches whenever the radar-container
  // DOM node mounts. A useRef + useEffect with `[]` deps misses the live-mode
  // flow, where render-1 returns `<Progress>` (no container in the DOM) and
  // render-2 mounts the container — by then the empty-deps effect has already
  // run with `ref.current === null` and never re-fires.
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const [containerSize, setContainerSize] = useState({ height: 0, width: 0 });
  const [searchText, setSearchText] = useState(props.initialSearchText ?? '');

  const isTechRadar = isTechRadarEntity(entity);
  const specData = isTechRadar ? entity.spec.data : undefined;
  const fetchMode = isTechRadar ? entity.spec.fetchMode : undefined;
  const dataFile = isTechRadar ? entity.spec.dataFile : undefined;

  // Hooks MUST run unconditionally on every render. The live-mode branch
  // gates on `fetchMode === 'live'` to avoid issuing a fetch for cached
  // entities, but the hook itself is always called.
  const liveDataFile = fetchMode === 'live' ? dataFile : undefined;
  const {
    data: liveData,
    error: liveError,
    loading: liveLoading,
  } = useLiveRadarData(liveDataFile);

  // The radar render-path is shared between cached and live: both ultimately
  // feed a `TechRadarLoaderResponse` into `CatalogTechRadarApi` so the
  // upstream `TechRadarComponent` can pick it up via `techRadarApiRef`.
  const transformedData: TechRadarLoaderResponse | null = useMemo(() => {
    if (fetchMode === 'live') {
      return liveData ?? null;
    }
    return specData ? transformRadarData(specData) : null;
  }, [fetchMode, liveData, specData]);
  const catalogApi = useMemo(
    () => (transformedData ? new CatalogTechRadarApi(transformedData) : null),
    [transformedData],
  );

  useEffect(() => {
    if (!containerEl) return;

    // Guard against state-updates after unmount: ResizeObserver may fire its
    // callback asynchronously after we have already disconnected (e.g. during
    // fast HMR / route-change) which would otherwise trigger a React
    // "state update on unmounted component" warning in Strict Mode.
    let cancelled = false;
    const observer = new ResizeObserver(entries => {
      if (cancelled) return;
      const entry = entries[0];
      if (entry) {
        setContainerSize({
          height: Math.floor(entry.contentRect.height),
          width: Math.floor(entry.contentRect.width),
        });
      }
    });
    observer.observe(containerEl);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [containerEl]);

  if (fetchMode === 'live') {
    if (liveLoading) {
      return (
        <section className={styles['radar-tile']} style={tileStyle}>
          <Progress />
        </section>
      );
    }
    if (liveError) {
      return (
        <section className={styles['radar-tile']} style={tileStyle}>
          <ResponseErrorPanel error={liveError} />
        </section>
      );
    }
    if (!liveData) {
      return (
        <section className={styles['radar-tile']} style={tileStyle}>
          <ResponseErrorPanel
            error={new Error('Live TechRadar data is unavailable.')}
          />
        </section>
      );
    }
    // Fall through to the shared render-path below; `transformedData` and
    // `catalogApi` have already been derived from `liveData`.
  } else if (!specData) {
    return (
      <section className={styles['radar-tile']} style={tileStyle}>
        <Alert
          icon
          aria-live="assertive"
          role="alert"
          status="danger"
          title="Geen radar data beschikbaar. Controleer of het JSON bestand correct is geconfigureerd."
        />
      </section>
    );
  }

  // Fit the radar to whichever dimension of the container is the binding
  // constraint: if height/0.65 is smaller than width, height limits the
  // radar; otherwise width does.
  const heightDerivedWidth = Math.floor(
    containerSize.height / RADAR_HEIGHT_TO_WIDTH,
  );
  const radarWidth =
    containerSize.height > 0
      ? Math.min(containerSize.width, heightDerivedWidth)
      : containerSize.width;
  const radarHeight = Math.round(radarWidth * RADAR_HEIGHT_TO_WIDTH);

  return (
    <section className={styles['radar-tile']} style={tileStyle}>
      <div className={styles.toolbar}>
        <label className={styles['search-label']}>
          <span className={styles['search-label-text']}>
            Filter radar entries
          </span>
          <input
            className={styles['search-input']}
            onChange={event => setSearchText(event.target.value)}
            placeholder="Filter radar entries"
            type="search"
            value={searchText}
          />
        </label>
      </div>
      <div ref={setContainerEl} className={styles['radar-container']}>
        {radarWidth > 0 && catalogApi && (
          <TechRadarApiScope catalogTechRadarApi={catalogApi}>
            <TechRadarComponent
              height={radarHeight}
              searchText={searchText}
              width={radarWidth}
            />
          </TechRadarApiScope>
        )}
      </div>
    </section>
  );
}
